import { log } from '@domgen/shared/utils/log';
import { EventObject } from '@xstate/store-react';
import { StoreEvent } from '../../../enums/storeEvent';
import { StoreContext } from '../../../models/store/context';
import { saveSession } from '../../../services/session/save';
import { AppStatus } from '../../../enums/appStatus';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { ApplianceModel } from '@domgen/shared/models/services/api/applianceLookup';
import { getCategoryFromApplianceCodeInCatalogue } from '../../../utils/store/getCategoryFromApplianceCodeInCatalogue';

/**
 * @description Set URL Parameter Results
 * This event is responsible for setting the results of URL parameter processing in the store context. It takes the appliances and matched appliance data from the event, updates the form data in the context with the model number and serial number from the URL parameters, and if a matched appliance is found, it also updates the form with the item category and super category based on the matched appliance's item type code. Finally, it saves the updated session context after setting the URL parameter results.
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data: { appliances: ApplianceModel[]; matchedAppliance?: ApplianceModel } }} event - The event object containing the appliances and matched appliance data in event.data
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: string; replace?: boolean }) => void } }} _enqueue - An object with an effect function to enqueue side effects and an emit function to navigate (not used in this case)
 * @returns {StoreContext} Updated context after setting URL parameter results and updating form data accordingly
 */
export const setParamResult = <T extends EventObject | undefined = EventObject>(
  context: StoreContext,
  event: T extends EventObject
    ? { data: { appliances: ApplianceModel[]; matchedAppliance?: ApplianceModel } }
    : never,
  _enqueue: {
    effect: (effect: () => Promise<void>) => void;
  },
): StoreContext => {
  log.debug({ message: StoreEvent.SET_URL_PARAM_RESULTS, data: event.data });
  const { appliances, matchedAppliance } = event.data;
  const mn = context.urlParams?.mn;
  const sn = context.urlParams?.sn;

  const form = { ...context.form };

  form[StepKey.MODEL_SERIAL] = {
    [FormKey.MODEL_NUMBER]: mn,
    [FormKey.SERIAL_NUMBER]: sn,
  };

  if (matchedAppliance && context.app.catalogue) {
    const category = getCategoryFromApplianceCodeInCatalogue(
      matchedAppliance.itemTypeCode,
      context.app.catalogue,
    );

    form[StepKey.APPLIANCE_DETAILS] = {
      [FormKey.ITEM_CATEGORY]: matchedAppliance.itemTypeCode,
      [FormKey.SUPER_CATEGORY]: category?.categoryCode,
    };
  }

  const updatedContext = {
    ...context,
    form,
    urlParams: {
      ...context.urlParams,
      matchedAppliance,
    },
    applianceLookupResults: appliances,
    app: { ...context.app, status: AppStatus.READY },
  };

  saveSession(updatedContext);
  return updatedContext;
};
